// Asset Radar: shared pure helpers ported from static/asset_radar/{core,facet_norm,people}.js.
// No React/DOM here — just the domain logic so every tab can use the same rules the old page used.
import type {
  ArAsset, ArCalendarRow, ArGeoCC, ArGeoLookups, ArGeoWmap, ArIndexAllEntry, ArIndication, ArLandscape, ArLandscapeRow,
  ArTrackerAsset,
} from '../../types';

export const TIER_NAME: Record<number, string> = { 1: 'Standard of care', 2: 'Approved, restricted', 3: 'Emerging' };
export const ST_LABEL: Record<string, string> = { mature: 'Mature data', early: 'Early data', none: 'No data yet' };
export const TIER_COLOR: Record<number, string> = { 1: '#0e7490', 2: '#6366f1', 3: '#ea580c' };
export const DATA_STATUS_COLOR: Record<string, string> = { mature: '#16a34a', early: '#f59e0b', none: '#94a3b8' };

const MON: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

export function parseDate(s: string | null | undefined): Date | null {
  const str = (s == null ? '' : String(s)).trim();
  if (!str) return null;
  let m = str.match(/(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, m[3] ? +m[3] : 15);
  m = str.match(/Q([1-4])\s*[-/ ]?\s*(\d{4})/i);
  if (m) return new Date(+m[2], (+m[1] - 1) * 3 + 1, 15);
  m = str.match(/([A-Za-z]{3,})\.?\s+(\d{4})/);
  if (m) {
    const k4 = m[1].slice(0, 4).toLowerCase(), k3 = m[1].slice(0, 3).toLowerCase();
    if (MON[k4] != null) return new Date(+m[2], MON[k4], 15);
    if (MON[k3] != null) return new Date(+m[2], MON[k3], 15);
  }
  m = str.match(/^(\d{4})$/) || str.match(/\b(20[1-4]\d)\b/);
  if (m) return new Date(+m[1], 6, 1);
  return null;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const norm = (s: unknown) => (s == null ? '' : String(s)).trim();

export interface TrackerRow {
  asset: string; alias: string; sponsor: string; target: string; moa: string; modality: string; status: string;
  res: RegExp[]; keyLen: number;
}

export function buildTracker(rows: ArTrackerAsset[]): TrackerRow[] {
  const out: TrackerRow[] = [];
  for (const r of rows || []) {
    const asset = norm(r.asset);
    if (!asset) continue;
    const aliases = (r.aliases || []).map(norm);
    const keys = new Set(
      [asset, ...aliases]
        .map((x) => norm(x).toLowerCase())
        .filter((x) => x.length >= 3 && x !== '—' && !/^(n\/a|none)$/.test(x)),
    );
    const row: TrackerRow = {
      asset, alias: aliases.join('; '), sponsor: norm(r.sponsor), target: norm(r.target),
      moa: norm(r.moa), modality: norm(r.modality), status: norm(r.status),
      res: [...keys].map((k) => new RegExp('(^|[^a-z0-9])' + esc(k) + '($|[^a-z0-9])', 'i')),
      keyLen: Math.max(...[...keys].map((k) => k.length), 0),
    };
    out.push(row);
  }
  return out;
}

export function trackerMatch(tracker: TrackerRow[] | null, text: string): TrackerRow | null {
  if (!tracker || !text) return null;
  let best: TrackerRow | null = null, bestPos = Infinity;
  for (const r of tracker) {
    for (const re of r.res) {
      const m = re.exec(text);
      if (m && (m.index < bestPos || (m.index === bestPos && best && r.keyLen > best.keyLen))) {
        best = r; bestPos = m.index;
      }
    }
  }
  return best;
}

export function heurMod(t: string): string {
  t = norm(t);
  if (/antibody.?drug conjugate|\bADC\b|vedotin|deruxtecan|govitecan|tecan\b/i.test(t)) return 'ADC';
  if (/bispecific|trispecific|engager|\bTCE\b|x\s?CD3|×\s?CD3|CD3\s?x/i.test(t)) return 'TCE / bispecific';
  if (/CAR-?T|CAR-?NK|cell therapy|\bTIL\b|TCR-T/i.test(t)) return 'Cell therapy';
  if (/radioligand|radiopharm|177Lu|225Ac|lutetium|actinium/i.test(t)) return 'Radioligand';
  if (/vaccine|mRNA/i.test(t)) return 'Vaccine';
  if (/PD-?1|PD-?L1|CTLA-?4|LAG-?3|TIGIT|checkpoint/i.test(t)) return 'IO';
  if (/inhibitor|degrader|PROTAC|small molecule|\bTKI\b|-nib\b|-lisib\b|-clax\b/i.test(t)) return 'Small molecule';
  if (/azacitidine|decitabine|platin|taxel|cytarabine|chemo|gemcitabine|FOLFOX|anthracycline/i.test(t)) return 'Chemotherapy';
  if (/antibody|\bmAb\b|mab\b|ligand trap|-Fc/i.test(t)) return 'mAb / biologic';
  if (/oncolytic/i.test(t)) return 'Oncolytic';
  return 'Other';
}

export function assetMod(tracker: TrackerRow[] | null, a: ArAsset): string {
  const m = trackerMatch(tracker, a.name);
  return (m && m.modality) || heurMod(`${a.moa} ${a.name}`);
}

export function rowMod(tracker: TrackerRow[] | null, r: { regimen: string; trial: string }): string {
  const m = trackerMatch(tracker, `${r.regimen} ${r.trial}`);
  return (m && m.modality) || heurMod(`${r.regimen} ${r.trial}`);
}

export const REGIONS: [string, RegExp][] = [
  ['China', /beigene|beone|innovent|akeso|hengrui|kelun|remegen|duality bio|baili|systimmune|junshi|lanova|cstone|henlius|alphamab|genor|hutchmed|hutchison|biokin|ascentage|innocare|chia tai|tianqing|cttq|hansoh|biocity|i-mab|tj bio|genescience|mabwell|qilu|cspc|zai lab|simcere|kintor|harbour biomed|legend biotech|lupeng|allist|dizal|haihe|zelgen|chipscreen|3d medicines|fosun|xuanzhu|maxinovel|targetrx|glubio|andao|antengene|jacobio|genfleet|betta|luye|haisco|bioray|gracell|carsgen|jw therapeutics|iaso|oricell|immunochina|keymed|biotheus|bio-thera|sichuan|jiangsu|shanghai|beijing|zhejiang|hangzhou|suzhou|guangzhou|shenzhen|chongqing|nanjing|tianjin|wuhan|shandong|xiamen|pumch|peking|fudan|sun yat|china|chinese/i],
  ['Japan', /daiichi|astellas|\bono\b|ono pharm|takeda|chugai|eisai|otsuka|taiho|kyowa|sumitomo|shionogi|chordia|symbio|nippon shinyaku|japan/i],
  ['Korea', /yuhan|hanmi|celltrion|abl bio|ligachem|legochem|samsung|boryung|\bhlb\b|elevar|gc cell|alteogen|korea/i],
];
export const REGION_COLOR: Record<string, string> = { China: '#dc2626', Japan: '#2563eb', Korea: '#7c3aed', Global: '#475569' };
export const regionOf = (sp: string): string => {
  for (const [r, re] of REGIONS) if (re.test(sp)) return r;
  return 'Global';
};
export function cleanSponsor(sp: string): string {
  let t = norm(sp).split(/;|\s\/\s|\(|\bvia\b/)[0];
  t = t.replace(/,?\s*\b(inc|ltd|co|llc|plc|gmbh|ag|corporation|corp|limited|company)\b\.?,?/gi, '').replace(/[,.\s]+$/, '').trim();
  return t || 'Sponsor NR';
}
export function firstAgent(t: string): string {
  return norm(t).split(/\s+(?:\+|vs\.?|±|\+\/-|with|plus)\s+|\s\(|,|;/i)[0].replace(/\[.*?\]/g, '').trim();
}
export function phaseBucket(p: string): string {
  p = norm(p);
  if (/approv/i.test(p)) return 'Approved';
  if (/3/.test(p)) return 'Phase 3';
  if (/2/.test(p) && /1/.test(p)) return 'Phase 1/2';
  if (/2/.test(p)) return 'Phase 2';
  if (/1/.test(p)) return 'Phase 1';
  return 'Not stated';
}
export const PHASE_ORDER = ['Approved', 'Phase 3', 'Phase 2', 'Phase 1/2', 'Phase 1', 'Not stated'];

export const PH_COL: Record<string, string> = { '1': '#C6DDF5', '12': '#93BEEA', '2': '#5B9BE0', '23': '#2F78C8', '3': '#1A56A0', '4': '#0E3A73', na: '#CBD2DB' };
export function phBucket(p: string): string {
  const t = norm(p);
  if (/approv|ph(?:ase)?\s*4|\biv\b/i.test(t)) return '4';
  const d = [...new Set((t.match(/[1-3]/g) || []).map(Number))].sort();
  if (!d.length) return 'na';
  if (d.length > 1) return d[d.length - 1] === 3 ? '23' : '12';
  return String(d[0]);
}

// ---------------- facet_norm.js ----------------
const sv = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v)).replace(/\s+/g, ' ').trim();

export const PHASES = ['Ph1', 'Ph1/2', 'Ph2', 'Ph2/3', 'Ph3', 'Ph4/Approved'];

export function phaseNorm(ctgov?: string | null, workbook?: string | null): string {
  for (const raw of [ctgov, workbook]) {
    const t = sv(raw).toLowerCase().split(/;|\bongoing\b|\bplanned\b/)[0];
    if (!t || /^(na|n\/a|not applicable|nr)$/.test(t)) continue;
    if (/approv|ph(ase)?\s*4|\biv\b|marketed/.test(t)) return 'Ph4/Approved';
    const d = new Set<number>();
    for (const m of t.matchAll(/ph(?:ase)?\s*(?:(iii|ii|i)\b|([1-3])(?!\d))/g)) {
      d.add(m[1] ? ({ i: 1, ii: 2, iii: 3 } as Record<string, number>)[m[1]] : +m[2]);
    }
    for (const m of t.matchAll(/(?<!\d)([1-3])\s*\/\s*(?:ph(?:ase)?\s*)?([1-3])(?!\d)/g)) { d.add(+m[1]); d.add(+m[2]); }
    if (!d.size) continue;
    const hi = Math.max(...d), lo = Math.min(...d);
    if (hi === lo) return `Ph${hi}`;
    return hi === 3 ? 'Ph2/3' : 'Ph1/2';
  }
  return '';
}

const PARENTS: [RegExp, string][] = [
  [/genentech|hoffmann.?la.?roche|\broche\b|chugai|foundation medicine/i, 'Roche'],
  [/janssen|johnson\s*&\s*johnson|\bj&j\b|actelion|ambrx/i, 'Johnson & Johnson'],
  [/merck sharp|\bmsd\b|^merck\b(?!\s*kgaa)/i, 'Merck & Co. (MSD)'],
  [/merck kgaa|emd serono|serono/i, 'Merck KGaA'],
  [/bristol.?myers|\bbms\b|celgene|juno|mirati|karuna|turning point/i, 'Bristol Myers Squibb'],
  [/astrazeneca|medimmune|alexion|acerta/i, 'AstraZeneca'],
  [/innocare/i, 'InnoCare'],
  [/novartis|sandoz|advanced accelerator/i, 'Novartis'],
  [/pfizer|seagen|seattle genetics|array biopharma|wyeth/i, 'Pfizer'],
  [/abbvie|pharmacyclics|allergan|immunogen/i, 'AbbVie'],
  [/\bgilead|kite pharma|\bkite\b|immunomedics/i, 'Gilead'],
  [/eli lilly|\blilly\b|loxo/i, 'Eli Lilly'],
  [/amgen/i, 'Amgen'],
  [/sanofi|genzyme/i, 'Sanofi'],
  [/takeda|millennium/i, 'Takeda'],
  [/daiichi/i, 'Daiichi Sankyo'],
  [/astellas/i, 'Astellas'],
  [/glaxo|\bgsk\b|tesaro/i, 'GSK'],
  [/bayer/i, 'Bayer'],
  [/beigene|beone/i, 'BeOne (BeiGene)'],
  [/incyte|morphosys/i, 'Incyte'],
  [/genmab/i, 'Genmab'],
  [/regeneron/i, 'Regeneron'],
  [/jiangsu hengrui|hengrui/i, 'Hengrui'],
  [/national cancer institute|\bnci\b/i, 'National Cancer Institute (NCI)'],
];
export function sponsorGroup(name?: string | null): string {
  const t = sv(name);
  if (!t) return '';
  for (const [rx, parent] of PARENTS) if (rx.test(t)) return parent;
  const c = t.split(/;|\s\/\s|\(/)[0]
    .replace(/,?\s*\b(inc|ltd|co|llc|plc|gmbh|ag|s\.?a|corporation|corp|limited|company|pharmaceuticals?|holdings?)\b\.?,?/gi, ' ')
    .replace(/[,.\s]+$/, '').replace(/\s+/g, ' ').trim();
  return c || t;
}

export function facetCounts<T>(rows: T[], sel: Record<string, string>, k: string, valOf: (r: T, key: string) => string): [string, number][] {
  const m = new Map<string, number>();
  for (const r of rows) {
    if (!Object.keys(sel).every((o) => o === k || !sel[o] || valOf(r, o) === sel[o])) continue;
    const v = valOf(r, k);
    if (v) m.set(v, (m.get(v) || 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

// ---------------- people.js ----------------
const BAD_CONTACT = /trial|study|studies|information|call cent|contact|pharma|\binc\b|\bltd\b|\bllc\b|gmbh|\bcorp|\bs\.?a\.?$|\bag$|clinical|recruit|research|center|centre|hospital|office|department|medical (monitor|director|information|affairs)|sponsor|helpline|hotline|\bdesk\b|\bteam\b|\bgroup\b|global|patient|inquir|enquir|novartis|pfizer|roche|genentech|astrazeneca|merck|bristol|amgen|abbvie|janssen|gilead|lilly|sanofi|bayer|takeda|daiichi|beigene|@|\d{3}/i;
const DEGREES = /\s*[,;]?\s*\b(dr|prof|professor|md|m\.d|phd|ph\.d|mbbs|mbchb|frcp|frcpc|facp|fracp|msc|mph|mba|bsc|rn|np|pharmd)\b\.?/gi;

export function cleanPerson(n: string | null | undefined): string | null {
  const t = sv(n).replace(DEGREES, ' ').replace(/^(dr|prof)\.?\s+/i, '').replace(/[,.\s]+$/, '').replace(/^[,.\s]+/, '').replace(/\s+/g, ' ').trim();
  if (t.length < 4 || BAD_CONTACT.test(t) || !/[\s,]/.test(t)) return null;
  return t;
}

const roleTitle = (r: string) => sv(r).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface PersonEntry {
  name: string; kind: 'official' | 'pi' | 'subi' | 'contact' | 'central';
  label: string; affiliation: string; facility: string; loc?: unknown;
}

export function peopleOf(t: { officials?: { name: string; role: string; affiliation: string }[]; locations?: { facility: string; contacts?: (string | { name: string; role?: string })[] }[]; central_contacts?: { name: string; role: string }[] } | null | undefined): PersonEntry[] {
  const out: PersonEntry[] = [];
  for (const o of t?.officials || []) {
    const name = cleanPerson(o?.name); if (!name) continue;
    out.push({ name, kind: 'official', label: `Overall official · ${roleTitle(o.role) || 'Role not stated'}`, affiliation: sv(o.affiliation), facility: '' });
  }
  for (const l of t?.locations || []) {
    // Python's ctgov_client map_location() only ever produces `contacts`, never a `people` field.
    const raws = (l.contacts || []).map((c) => (typeof c === 'string' ? { name: c, role: '' } : { name: c?.name || '', role: c?.role || '' }));
    for (const c of raws) {
      const name = cleanPerson(c.name); if (!name) continue;
      const role = sv(c.role).toUpperCase();
      const f = sv(l.facility);
      const kind: PersonEntry['kind'] = /SUB_?INVESTIGATOR/.test(role) ? 'subi' : /PRINCIPAL|INVESTIGATOR/.test(role) ? 'pi' : /CHAIR|DIRECTOR/.test(role) ? 'pi' : 'contact';
      const what = { pi: 'Site PI', subi: 'Sub-investigator', contact: 'Site contact' }[kind as 'pi' | 'subi' | 'contact'];
      out.push({ name, kind, label: `${what}${f ? ' · ' + f : ''}`, affiliation: f, facility: f, loc: l });
    }
  }
  for (const c of t?.central_contacts || []) {
    const name = cleanPerson(c?.name); if (!name) continue;
    out.push({ name, kind: 'central', label: 'Central contact (sponsor)', affiliation: '', facility: '' });
  }
  return out;
}

export function nameKey(n: string): string {
  const t = sv(n).normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z\s,-]/g, ' ');
  let parts = t.includes(',') ? [...t.split(',').slice(1).join(' ').split(/\s+/), t.split(',')[0]] : t.split(/\s+/);
  parts = parts.map((x) => x.replace(/-/g, '')).filter(Boolean);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : parts.join(' ');
}

export const KIND_RANK: Record<string, number> = { official: 0, pi: 1, subi: 2, contact: 3, central: 5 };

export const siteKey = (l: { facility: string; city: string }) =>
  `${norm(l.facility).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()}|${norm(l.city).toLowerCase()}`;

// CT.gov overall_status -> a short label + a semantic color bucket (recruiting/active/done/stopped/unknown).
export const REG_STATUS: Record<string, { label: string; bucket: 'go' | 'active' | 'done' | 'stop' | 'unknown' }> = {
  RECRUITING: { label: 'Recruiting', bucket: 'go' },
  NOT_YET_RECRUITING: { label: 'Not yet recruiting', bucket: 'unknown' },
  ENROLLING_BY_INVITATION: { label: 'Enrolling by invitation', bucket: 'go' },
  ACTIVE_NOT_RECRUITING: { label: 'Active, not recruiting', bucket: 'active' },
  COMPLETED: { label: 'Completed', bucket: 'done' },
  SUSPENDED: { label: 'Suspended', bucket: 'stop' },
  TERMINATED: { label: 'Terminated', bucket: 'stop' },
  WITHDRAWN: { label: 'Withdrawn', bucket: 'stop' },
  UNKNOWN: { label: 'Unknown status', bucket: 'unknown' },
};
export const STATUS_BUCKET_COLOR: Record<string, string> = {
  go: '#16a34a', active: '#2563eb', done: '#64748b', stop: '#dc2626', unknown: '#cbd5e1',
};
export function regLabel(st?: string | null): string {
  if (!st) return 'Not in registry';
  return REG_STATUS[st]?.label || st.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}
export function regColor(st?: string | null): string {
  if (!st) return STATUS_BUCKET_COLOR.unknown;
  return STATUS_BUCKET_COLOR[REG_STATUS[st]?.bucket || 'unknown'];
}

// CT.gov overall_status -> the `.rs-*` pill class already styled in radar.css.
const REG_CLASS: Record<string, string> = {
  RECRUITING: 'rs-go', NOT_YET_RECRUITING: 'rs-soon', ENROLLING_BY_INVITATION: 'rs-go',
  ACTIVE_NOT_RECRUITING: 'rs-active', COMPLETED: 'rs-done', SUSPENDED: 'rs-stop',
  TERMINATED: 'rs-stop', WITHDRAWN: 'rs-stop', UNKNOWN: 'rs-done',
};
export function regClass(st?: string | null): string {
  return (st && REG_CLASS[st]) || 'rs-done';
}

// ---------------- world map (wmap.js) ----------------
// Maps a raw ClinicalTrials.gov country string (any casing/alias) to the canonical name used by
// wmap.json's `c`/`x` lists, so the map, the "Where" select and the site/table filters all agree
// on one name per country instead of splintering "USA" / "United States of America" / etc.
export function canonicalCountryMap(geo: { wmap: ArGeoWmap; lookups: ArGeoLookups }): Map<string, string> {
  const m = new Map<string, string>();
  geo.wmap.c.forEach(([name]) => m.set(name.toLowerCase(), name));
  geo.wmap.x.forEach(([name]) => { if (!m.has(name.toLowerCase())) m.set(name.toLowerCase(), name); });
  Object.entries(geo.lookups.WM_ALIAS || {}).forEach(([alias, canon]) => { if (!m.has(alias)) m.set(alias, canon); });
  return m;
}

// ---------------- offline city gazetteer (wmcity.json / wmcc.json) ----------------
// Resolves a site's facility city/state/country to the map's [x,y] drawing space without any network
// geocoding call — matches the artifact's offline gazetteer approach, just with our own pre-generated
// wmcity.json (ISO2 -> cities) / wmcc.json (country name -> ISO2) static assets.

/** Parses one wmcity.json entry's ';'-separated "name|admin1|x|y" list into a lookup map. US cities are
 * keyed "name|STATE" (admin1 non-empty); every other country's cities are keyed by name alone. */
export function parseCityEntries(raw: string | undefined): Map<string, [number, number]> {
  const m = new Map<string, [number, number]>();
  if (!raw) return m;
  for (const entry of raw.split(';')) {
    const [name, admin1, xs, ys] = entry.split('|');
    if (!name || xs === undefined || ys === undefined) continue;
    const x = +xs, y = +ys;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const key = admin1 ? `${name}|${admin1}` : name;
    if (!m.has(key)) m.set(key, [x, y]);
  }
  return m;
}

const CITY_SUFFIX_RE = /(-shi|-ku|\s+cedex)$/i;

function normCityName(s: string | undefined): string {
  return (s || '')
    .normalize('NFKD').replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[.,'’()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stSaintSwap(s: string): string | null {
  if (/\bst\b/.test(s)) return s.replace(/\bst\b/, 'saint');
  if (/\bsaint\b/.test(s)) return s.replace(/\bsaint\b/, 'st');
  return null;
}

export interface SiteGeoContext {
  cc: ArGeoCC;
  cityByIso: Map<string, Map<string, [number, number]>>;
  canon: Map<string, string>;
  usSt: Record<string, string>;
  gzAlias: Record<string, string>;
}

/** Resolves a facility to [x,y] in the map's drawing space, degrading to `null` ("unplaced") at every
 * step rather than throwing on missing/odd data — country not recognized, no city list for that
 * country, or no name variant matches. */
export function resolveSiteXY(site: { city?: string; state?: string; country?: string }, ctx: SiteGeoContext): [number, number] | null {
  const rawCountry = (site.country || '').trim();
  if (!rawCountry) return null;
  const canonCountry = ctx.canon.get(rawCountry.toLowerCase()) || rawCountry;
  const iso2 = ctx.cc[canonCountry.toLowerCase()];
  if (!iso2) return null;
  const cities = ctx.cityByIso.get(iso2);
  if (!cities || !cities.size) return null;

  const isUS = iso2 === 'US';
  let stAbbrev = '';
  if (isUS) {
    const raw = (site.state || '').trim();
    stAbbrev = raw.length === 2 ? raw.toUpperCase() : (ctx.usSt[raw.toLowerCase()] || '');
  }
  const buildKey = (name: string) => (isUS && stAbbrev ? `${name}|${stAbbrev}` : name);
  const tryName = (name: string): [number, number] | undefined => cities.get(buildKey(name));

  const name0 = normCityName(site.city);
  if (!name0) return null;

  let hit = tryName(name0);
  if (hit) return hit;

  const alias = ctx.gzAlias[name0];
  if (alias) { hit = tryName(alias); if (hit) return hit; }

  const stripped = name0.replace(CITY_SUFFIX_RE, '').trim();
  if (stripped && stripped !== name0) { hit = tryName(stripped); if (hit) return hit; }

  const swapped = stSaintSwap(name0);
  if (swapped) { hit = tryName(swapped); if (hit) return hit; }
  if (stripped) {
    const swappedStripped = stSaintSwap(stripped);
    if (swappedStripped) { hit = tryName(swappedStripped); if (hit) return hit; }
  }

  const firstWord = name0.split(' ')[0];
  if (firstWord && firstWord !== name0) { hit = tryName(firstWord); if (hit) return hit; }

  return null;
}

export const norm_ = norm;

// ---------------- "By target" cross-disease asset explorer ----------------
// Ported from the reference Asset Radar artifact's globalAssets()/indexWb()/splitTargets() — but sourced
// from our own /api/index-all (every synced disease's parsed workbook in one call) instead of the
// artifact's own per-disease progressive Google Sheets fetch, since our backend already assembles that
// server-side. See arGetIndexAll() in services/api.ts.

/** tracker.js-style compound-target splitter: "PD-1/PD-L1" -> ["PD-1","PD-L1"]. */
export function splitTargets(t: string): string[] {
  const v = norm_(t);
  if (!v || v === '—') return ['Target not stated'];
  return [...new Set(
    v
      .split(/\s*(?:\/|,|;|\+|&|\bx\b|×|\band\b)\s*/i)
      .map((x) => x.replace(/^[-–\s]+|[-–\s]+$/g, ''))
      .filter((x) => x.length > 1),
  )];
}

export type ArDataState = 'With clinical data' | 'Trials only, no data yet' | 'Not in a loaded landscape';

export interface ArAssetApp { disease: string; ind: ArIndication; a: ArAsset }
export interface ArAssetTrial { disease: string; ind: ArIndication; r: ArCalendarRow }

/** One row of the global asset index: either a curated Target-MOA Tracker entry (`inTracker: true`,
 * key `t:<asset>`), or an untracked Emerging-tier landscape asset with no tracker match (key
 * `n:<firstAgent(name)>`) — untracked calendar-only (trial) rows are never indexed on their own,
 * matching the artifact's behavior exactly (see buildGlobalAssets). */
export interface GlobalAssetEntry {
  key: string;
  name: string;
  alias: string;
  sponsor: string;
  moa: string;
  modality: string;
  target: string;
  inTracker: boolean;
  apps: ArAssetApp[];
  trials: ArAssetTrial[];
  diseases: Set<string>;
  phases: Set<string>;
  lead: boolean;
  /** Derived (post-pass) fields. */
  phase: string;
  region: string;
  targets: string[];
  dataState: ArDataState;
  hay: string;
}

type GlobalAssetSeed = Pick<GlobalAssetEntry, 'name' | 'alias' | 'sponsor' | 'moa' | 'modality' | 'target' | 'inTracker'>;

/** Builds the global, cross-disease asset index from every synced workbook (api/index-all) plus the
 * curated Target-MOA Tracker sheet — the "By target" view's core data source. Pure/testable: no
 * fetching, no React. */
export function buildGlobalAssets(
  entries: ArIndexAllEntry[],
  diseaseLabels: Map<string, string>,
  tracker: TrackerRow[] | null,
): GlobalAssetEntry[] {
  const byKey = new Map<string, GlobalAssetEntry>();
  const ent = (key: string, init: GlobalAssetSeed): GlobalAssetEntry => {
    let e = byKey.get(key);
    if (!e) {
      e = {
        key, ...init,
        apps: [], trials: [], diseases: new Set(), phases: new Set(), lead: false,
        phase: 'Not stated', region: 'Global', targets: [], dataState: 'Not in a loaded landscape', hay: '',
      };
      byKey.set(key, e);
    }
    return e;
  };

  // Seed one entry per curated tracker row first, so landscape/calendar rows that match one just merge in.
  for (const r of tracker || []) {
    ent(`t:${r.asset.toLowerCase()}`, {
      name: r.asset, alias: r.alias, sponsor: cleanSponsor(r.sponsor), target: r.target, moa: r.moa,
      modality: r.modality, inTracker: true,
    });
  }

  for (const entry of entries) {
    const diseaseLabel = diseaseLabels.get(entry.disease) || entry.disease;
    for (const ind of entry.parsed?.inds || []) {
      for (const a of ind.land?.assets || []) {
        const m = tracker ? trackerMatch(tracker, a.name) : null;
        let e: GlobalAssetEntry | undefined;
        if (m) {
          e = byKey.get(`t:${m.asset.toLowerCase()}`);
        } else {
          // Untracked landscape rows only ever create a new entry for Emerging (tier 3) assets.
          if (a.tier !== 3) continue;
          const fa = firstAgent(a.name);
          e = ent(`n:${fa.toLowerCase()}`, {
            name: fa, alias: '', sponsor: cleanSponsor(a.sponsor), target: '', moa: a.moa,
            modality: assetMod(tracker, a), inTracker: false,
          });
        }
        if (!e) continue;
        e.diseases.add(diseaseLabel);
        e.apps.push({ disease: entry.disease, ind, a });
        e.phases.add(phaseBucket(a.phase));
        if (a.lead) e.lead = true;
      }
      for (const r of ind.cal || []) {
        // Untracked calendar-only rows are dropped entirely -- never create a new entry on their own.
        const m = tracker ? trackerMatch(tracker, `${r.regimen} ${r.trial}`) : null;
        if (!m) continue;
        const e = byKey.get(`t:${m.asset.toLowerCase()}`);
        if (!e) continue;
        e.diseases.add(diseaseLabel);
        e.trials.push({ disease: entry.disease, ind, r });
        e.phases.add(phaseBucket(r.phaseN || r.phase));
      }
    }
  }

  const out = [...byKey.values()];
  for (const e of out) {
    e.phase = PHASE_ORDER.find((p) => e.phases.has(p)) || 'Not stated';
    e.region = regionOf(e.sponsor);
    e.targets = splitTargets(e.target);
    e.moa = norm_(e.moa) || 'MOA not stated';
    e.modality = norm_(e.modality) || 'Other';
    e.dataState = e.apps.length ? 'With clinical data' : e.trials.length ? 'Trials only, no data yet' : 'Not in a loaded landscape';
    e.hay = [e.name, e.alias, e.sponsor, e.target, e.moa, e.modality, e.phase, e.region, ...e.diseases]
      .join(' ')
      .toLowerCase();
  }
  return out;
}

const NR_RE = /^NR$|^N\/A$/i;
function reportedCell(v: string | undefined): string {
  const t = (v || '').trim();
  return t && !NR_RE.test(t) ? t : '';
}

export interface ArMetric { label: string; value: string }

/** ORR / PFS / OS / Grade >=3, pulled straight out of a landscape's own reported cells for one asset's
 * column (at most one row per category, first match by row-label regex, skipping NR/N/A/empty cells). */
export function metricsFor(land: ArLandscape | null | undefined, a: ArAsset): ArMetric[] {
  if (!land) return [];
  const out: ArMetric[] = [];
  const pick = (re: RegExp, section: ArLandscapeRow['section'], label: string) => {
    const row = land.rows.find((r) => r.section === section && re.test(r.label));
    if (!row) return;
    const v = reportedCell(row.cells[a.idx]);
    if (v) out.push({ label, value: v });
  };
  pick(/\bORR\b|response rate|overall response|\bCR\b rate/i, 'efficacy', 'ORR');
  pick(/\bPFS\b|progression|\bEFS\b|\bRFS\b/i, 'efficacy', 'PFS');
  pick(/^\s*OS\b|overall survival/i, 'efficacy', 'OS');
  pick(/overall grade|grade\s*(?:≥|>=|≧)\s*3|g\s*(?:≥|>=)\s*3/i, 'safety', 'Grade ≥3');
  return out;
}
